<template>
  <!-- scroll-body off for the reason AdminApp.vue gives: <html> already
       reserves the scrollbar gutter, so reka's padding shifted the page. -->
  <ConfigProvider :scroll-body="false">
    <RouteProgress />
    <!-- Hidden on ServerConnect so it can't cover the screen for fixing an unreachable server. -->
    <OfflineOverlay v-if="route.name !== 'ServerConnect'" />
    <!-- Android-only: blocks the app until notifications are granted because
       the foreground IpPollService needs a persistent notification to stay
       alive. iOS has no equivalent dependency. Local notifications there
       are an optional expiry-warning convenience, so we still request
       permission once at startup but don't gate the UI on the answer. -->
    <NotificationPermissionOverlay v-if="isAndroidNative" />
    <BatteryOptimizationOverlay v-if="isAndroidNative" />
    <AppUpdateBanner v-if="(isAndroidNative || isIOSNative) && route.name !== 'ServerConnect'" />
    <ServiceKilledBanner v-if="isAndroidNative" />
    <VpnPermissionOverlay v-if="isIOSNative" />
    <router-view :key="route.path"></router-view>
    <!-- Every ErrorNotification and InfoNotification renders here. -->
    <Toaster position="bottom-right" :expand="true" :toast-options="{ unstyled: true }" />
  </ConfigProvider>
</template>

<script setup>
import { Toaster } from "@/components/ui/sonner";
import { ConfigProvider } from "reka-ui";
import { computed, onBeforeUnmount, onMounted, watch } from "vue";
import { useRoute } from "vue-router";
import { useColorMode } from "@vueuse/core";
import OfflineOverlay from "./components/OfflineOverlay.vue";
import NotificationPermissionOverlay from "./components/NotificationPermissionOverlay.vue";
import BatteryOptimizationOverlay from "./components/BatteryOptimizationOverlay.vue";
import AppUpdateBanner from "./components/AppUpdateBanner.vue";
import ServiceKilledBanner from "./components/ServiceKilledBanner.vue";
import RouteProgress from "./components/RouteProgress.vue";
import VpnPermissionOverlay from "./components/VpnPermissionOverlay.vue";
import { isAndroid, isIOS, isNativePlatform } from "./capacitor/platform";
import { syncNativeTheme } from "./capacitor/theme-sync";
import { rememberColorMode } from "./utils/shellColorMode";
import { useUserStatusQuery } from "./queries/session";
import { useAccentAttribute } from "./queries/theme";

const isAndroidNative = isAndroid();
const isIOSNative = isIOS();

const route = useRoute();

// The portal's primary is the accent the admin chose (utils/portalAccent.ts).
useAccentAttribute();

// Mounted so a write refetches the status. useStatusPolling sets the cadence.
useUserStatusQuery(computed(() => route.meta?.requiresUserLogin === true));

// Route external links through the system browser on native. On iOS in a
// Capacitor app, <a target="_blank"> would otherwise open in the in-app
// WebView with no back button, stranding the user. Skipping non-http(s)
// URLs (mailto:, tel:, knocknoc:, app-settings:) leaves them to iOS's URL
// handler, which is the desired behaviour for those.
async function handleNativeExternalClick(e) {
  const a = e.target?.closest?.('a[target="_blank"]');
  if (!a) return;
  const href = a.getAttribute("href");
  if (!href || !/^https?:/i.test(href)) return;
  e.preventDefault();
  try {
    const { Browser } = await import("@capacitor/browser");
    await Browser.open({ url: href });
  } catch (err) {
    console.warn("[App] Browser.open failed:", err);
  }
}

if (isNativePlatform()) {
  onMounted(() => document.addEventListener("click", handleNativeExternalClick));
  onBeforeUnmount(() => document.removeEventListener("click", handleNativeExternalClick));
}

// Apply system theme by default on native, dark on web.
// useColorMode persists the user's choice in localStorage,
// so this initialValue only applies on first launch.
const mode = useColorMode({
  initialValue: isNativePlatform() ? "auto" : "dark",
  emitAuto: true,
});

// The store value is the user preference ("light", "dark", "auto").
// The .value from useColorMode resolves "auto" to the actual system theme.
// When emitAuto is true, mode.value can be "auto" -- but mode.state is the resolved value.
const isDark = computed(() => {
  const resolved = mode.value;
  if (resolved === "dark") return true;
  if (resolved === "light") return false;
  // "auto" -- check what the system prefers
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
});

if (isNativePlatform()) {
  watch(isDark, (dark) => syncNativeTheme(dark), { immediate: true });
}

// Cookie so the server paints the matching shell on a cold load.
watch(isDark, rememberColorMode, { immediate: true });
</script>
